// OS detection for gating the "Get Desktop App" banner. Atlas Desktop ships
// for Windows and macOS, so the banner shows on those and stays hidden on
// Linux, Android, iOS and any phone/tablet. We detect the OS (not screen
// width) so it's reliable across tablets and resized windows.

// True when running in a browser on a Windows machine.
export function isWindows(): boolean {
  if (typeof navigator === 'undefined') return false;

  // Prefer the modern, high-signal hint (Chromium-based browsers).
  const uaData = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
  if (uaData?.platform) return uaData.platform === 'Windows';

  // Fallback to the user-agent / platform string. Exclude the legacy
  // Windows Phone so mobile never matches.
  const s = navigator.userAgent || navigator.platform || '';
  return /Windows NT|Win32|Win64|WOW64/i.test(s) && !/Windows Phone/i.test(s);
}

// True when running in a browser on a Mac.
//
// iPadOS is the trap: in its default "desktop" mode Safari reports platform
// "MacIntel" and a Mac user-agent, so a bare string test would show the banner
// on an iPad. Multi-touch is what separates them — no Mac reports more than
// one touch point.
export function isMacOS(): boolean {
  if (typeof navigator === 'undefined') return false;

  const isTouchDevice = (navigator.maxTouchPoints || 0) > 1;

  const uaData = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
  if (uaData?.platform) return uaData.platform === 'macOS' && !isTouchDevice;

  const s = navigator.userAgent || navigator.platform || '';
  // Exclude iPhone/iPod outright; iPad is caught by the touch check above.
  return /Mac/i.test(s) && !/iPhone|iPod/i.test(s) && !isTouchDevice;
}

// True when a desktop build exists for this machine's OS.
export function hasDesktopBuild(): boolean {
  return isWindows() || isMacOS();
}
